/**
 * Plan tree mutations.
 *
 * Each node's target is independent — no propagation to parents or children,
 * no auto-fill of special or New Product siblings. Sum-vs-target gap is shown
 * to the user via the ParentSummary delta indicator instead of being
 * auto-corrected.
 */

import type { PlanNode } from './PlanNodesPanel'

export function applyTargetEdit(
  roots: PlanNode[],
  nodeId: string,
  newTarget: number | null,
  opts: { editedGrowthPct?: number | null } = {}
): PlanNode[] {
  const clamped = newTarget == null ? null : Math.max(0, newTarget)
  return mapTree(roots, (n) => {
    if (n.id !== nodeId) return n
    const base = Number(n.revenue) || 0
    // On a quarterly revision the months already sold count towards growth;
    // a plain plan has no actualRevenue, so this is 0 there.
    const actual = Number(n.actualRevenue ?? 0)
    let growthPct: number | null
    if (opts.editedGrowthPct !== undefined) {
      growthPct = opts.editedGrowthPct
    } else if (clamped == null || base === 0) {
      growthPct = null
    } else {
      growthPct = Math.round(((actual + clamped - base) / base) * 10000) / 100
    }
    return { ...n, targetRevenue: clamped, growthPct }
  })
}

const isBucket = (n: PlanNode) => !!(n.special || n.isNewProduct)

/** Real nodes first, then the "Other" / "New Products" buckets at the end. */
function bucketsLast(siblings: PlanNode[]): PlanNode[] {
  return [...siblings.filter((n) => !isBucket(n)), ...siblings.filter(isBucket)]
}

export function applyAddNode(
  roots: PlanNode[],
  parentId: string | null,
  newNode: PlanNode
): PlanNode[] {
  if (parentId == null) return bucketsLast([...roots, newNode])
  return mapTree(roots, (n) =>
    n.id === parentId ? { ...n, children: bucketsLast([...n.children, newNode]) } : n
  )
}

export function applyRemoveNode(roots: PlanNode[], nodeId: string): PlanNode[] {
  const filterTree = (nodes: PlanNode[]): PlanNode[] =>
    nodes
      .filter((n) => n.id !== nodeId)
      .map((n) => ({ ...n, children: filterTree(n.children) }))
  return filterTree(roots)
}

/**
 * Recompute special "Other" nodes at a single level so their past revenue,
 * past share, and target track the current set of siblings. Mirrors the
 * server-side TopDownPlanNodeHydrator logic so local mutations stay in sync
 * between Inertia fetches.
 */
export function syncSpecialAtLevel(
  roots: PlanNode[],
  parentId: string | null,
  parentRevenue: number,
  parentShare: number
): PlanNode[] {
  const updateChildren = (children: PlanNode[]): PlanNode[] => {
    const nonBucketRevenue = children.reduce(
      (s, n) => s + (isBucket(n) ? 0 : Number(n.revenue) || 0),
      0
    )
    const nonBucketShare = children.reduce(
      (s, n) => s + (isBucket(n) ? 0 : n.share),
      0
    )
    return children.map((n) => {
      if (!n.special) return n
      const derivedRevenue = Math.max(parentRevenue - nonBucketRevenue, 0)
      const derivedShare = Math.max(parentShare - nonBucketShare, 0)
      const growth = n.growthPct ?? 0
      // Full-year growth: the target is what the growth implies for the
      // year, minus the months already sold (0 on a plain plan).
      const actual = Number(n.actualRevenue ?? 0)
      const derivedTarget = Math.max(0, Math.round(derivedRevenue * (1 + growth / 100) - actual))
      return {
        ...n,
        revenue: String(derivedRevenue),
        share: derivedShare,
        targetRevenue: derivedTarget
      }
    })
  }
  if (parentId == null) return updateChildren(roots)
  return roots.map((n) => {
    if (n.id === parentId) return { ...n, children: updateChildren(n.children) }
    return { ...n, children: syncSpecialAtLevel(n.children, parentId, parentRevenue, parentShare) }
  })
}

function mapTree(nodes: PlanNode[], fn: (n: PlanNode) => PlanNode): PlanNode[] {
  return nodes.map((n) => {
    const mapped = fn(n)
    return { ...mapped, children: mapTree(mapped.children, fn) }
  })
}
